<script setup>
import { ref, watch, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { CoursesClient } from "@/services/CoursesClient";
import { ChaptersClient } from "@/services/ChaptersClient";
import { LessonsClient } from "@/services/LessonsClient";
import { QuizzesClient } from "@/services/QuizzesClient";
import { ResourceClient } from "@/services/ResourceClient";
import { CompletedLessonClient } from "@/services/CompleteLessonClient";
import Swal from "sweetalert2";
import NotesModal from "@/views/student/components/Notes.vue";
import ChapterPage from "@/views/student/components/ChapterPage.vue";

const route = useRoute();
const router = useRouter();
const coursesClient = new CoursesClient();
const chaptersClient = new ChaptersClient();
const lessonsClient = new LessonsClient();
const quizzesClient = new QuizzesClient();
const resourceClient = new ResourceClient();
const completedLessonClient = new CompletedLessonClient();
const forbidden = ref(false);  

// Data state
const course = ref(null);
const courseId = computed(() => route.params.curso);
const currentChapter = ref(null);
const lesson = ref(null);
const loading = ref(true);
const isCompleting = ref(false);
const error = ref(null);
const files = ref([]);
const resources = ref([]);

const loadingResources = ref(false);

const chapters = ref([]);

// UI state
const isCompleted = ref(false);
const userNotes = ref("");
const notesModalRef = ref(null);

// Fetch all data
const fetchData = async () => {
  try {
    loading.value = true;

    // Fetch course
    const courseId = route.params.curso;
    const [courseResponse, chaptersResponse] = await Promise.all([
      coursesClient.getCourseById(courseId),
      chaptersClient.getAllChaptersByCourse(courseId),
    ]);

    course.value = courseResponse;
    chapters.value = chaptersResponse.sort((a, b) => a.orden - b.orden);

    // Set current chapter if in route
    if (route.params.capitulo) {
      currentChapter.value = chapters.value.find(
        chap => chap.id == route.params.capitulo
      );
    } else if (chapters.value.length > 0) {
      // Navigate to first chapter by default
      router.replace(`/cursos/${courseId}/${chapters.value[0].id}`);
      return;
    }

    // Fetch current lesson if in route
    if (route.params.leccion) {
      await fetchLessonData();
    }

  } catch (err) {   
    error.value = "Failed to load data";
    console.error("Error loading data:", err);
  } finally {
    loading.value = false;
  }
};

// Fetch current lesson data
const fetchLessonData = async () => {
  try {
    lesson.value = await lessonsClient.getLessonById(route.params.leccion);
    isCompleted.value = await completedLessonClient.isLessonCompleted(route.params.leccion);
  
    await fetchLessonVideos();
    await fetchLessonResources();

  } catch (err) {
    if (err.message.includes("403")) {
      forbidden.value = true;
    } else {
      error.value = "Error al cargar los datos. Por favor intenta nuevamente.";
    }
    console.error("Error loading lesson:", err);
  }
};

// Fetch lesson videos
const fetchLessonVideos = async () => {
  try {
    const response = await resourceClient.getResourceById(route.params.leccion, 'video');

    files.value = response.map(resource => ({
      id: resource.id,
      type: resource.tipo,
      name: resource.nombre_archivo,
      url: resource.url,
      isPreview: resource.es_visualizacion
    }));

    if (files.value.length > 0) {
      await loadVideo(files.value[0]);
    }
  } catch (error) {
    console.error("Error fetching videos:", error);
    Swal.fire('Error', 'No se pudieron cargar los videos', 'error');
  }
};

const fetchLessonResources = async () => {
  try {
    loadingResources.value = true;
    const response = await resourceClient.getResourceById(route.params.leccion, 'recurso');

    // Clear existing resources first
    resources.value = [];

    // Create array of promises for all resources
    const resourcePromises = response.map(resource => loadResource(resource));

    // Wait for all resources to load and add to resources array
    const loadedResources = await Promise.all(resourcePromises);
    resources.value = loadedResources.filter(Boolean); // Filter out any null/undefined

  } catch (error) {
    console.error("Error fetching resources:", error);
    Swal.fire('Error', 'No se pudieron cargar los recursos', 'error');
  } finally {
    loadingResources.value = false;
  }
};

const loadResource = async (resource) => {
  try {
    const url = `${process.env.VUE_APP_BACK_END_URL}/resource/download-url?key=${resource.url}`;
    const response = await fetch(url, {
      headers: {
        Accept: 'text/plain',
        Authorization: `Bearer ${sessionStorage.getItem('token') || ''}`
      }
    });

    const responseText = await response.text();

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    // Return the complete resource object
    return {
      id: resource.id,
      type: getFileType(resource.tipo || resource.url.split('.').pop()),
      name: resource.nombre_archivo,
      url: resource.url,
      downloadUrl: responseText,
      isPreview: resource.es_visualizacion,
      size: resource.size || 'N/A'
    };

  } catch (error) {
    console.error('❌ Error loading resource:', error);
    // Return resource without downloadUrl
    return {
      id: resource.id,
      type: getFileType(resource.tipo || resource.url.split('.').pop()),
      name: resource.nombre_archivo,
      url: resource.url,
      downloadUrl: null,
      isPreview: resource.es_visualizacion,
      size: resource.size || 'N/A'
    };
  }
};

const getFileIcon = (fileType) => {
  const icons = {
    'pdf': 'fas fa-file-pdf text-danger',
    'word': 'fas fa-file-word text-primary',
    'excel': 'fas fa-file-excel text-success',
    'powerpoint': 'fas fa-file-powerpoint text-warning',
    'text': 'fas fa-file-alt text-info',
    'image': 'fas fa-file-image text-primary',
    'video': 'fas fa-file-video text-info',
    'audio': 'fas fa-file-audio text-secondary',
    'archive': 'fas fa-file-archive text-warning',
    'file': 'fas fa-file text-secondary'
  };
  return icons[fileType] || icons['file'];
};

const getFileType = (mimeTypeOrExt) => {
  const typeMap = {
    // Documents
    'pdf': 'pdf',
    'application/pdf': 'pdf',
    'doc': 'word',
    'docx': 'word',
    'application/msword': 'word',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'word',
    'xls': 'excel',
    'xlsx': 'excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'excel',
    'ppt': 'powerpoint',
    'pptx': 'powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'powerpoint',
    'txt': 'text',
    'text/plain': 'text',

    // Images
    'jpg': 'image',
    'jpeg': 'image',
    'png': 'image',
    'gif': 'image',
    'svg': 'image',
    'image/jpeg': 'image',
    'image/png': 'image',
    'image/gif': 'image',
    'image/svg+xml': 'image',

    // Videos
    'mp4': 'video',
    'webm': 'video',
    'avi': 'video',
    'mov': 'video',
    'video/mp4': 'video',
    'video/webm': 'video',

    // Audio
    'mp3': 'audio',
    'wav': 'audio',
    'audio/mpeg': 'audio',
    'audio/wav': 'audio',

    // Archives
    'zip': 'archive',
    'rar': 'archive',
    '7z': 'archive',
    'application/zip': 'archive',
    'application/vnd.rar': 'archive',
    'application/x-7z-compressed': 'archive',

    // Executables
    'exe': 'exe',
    'application/x-msdownload': 'exe',

    // Default
    'default': 'file'
  };

  // Check if input is extension or mime type
  const key = mimeTypeOrExt.includes('/') ?
    mimeTypeOrExt.toLowerCase() :
    mimeTypeOrExt.toLowerCase().replace('.', '');

  return typeMap[key] || typeMap['default'];
};

// Load video player
const loadVideo = async (resource) => {
  try {
    const url = `${process.env.VUE_APP_BACK_END_URL}/resource/download-url?key=${resource.url}`;
    const response = await fetch(url, {
      headers: {
        Accept: 'text/plain',
        Authorization: `Bearer ${sessionStorage.getItem('token') || ''}`
      }
    });

    if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

    const responseText = await response.text();

    files.value = [{
      id: resource.id,
      type: 'video',
      name: resource.nombre_archivo,
      url: resource.url,
      downloadUrl: responseText,
      isPreview: true
    }];

  } catch (error) {
    console.error('Error loading video:', error);
    Swal.fire('Error', 'Failed to load video', 'error');
  }
};

// UI methods
const toggleComplete = async () => {
  try {
    isCompleting.value = true;

    if (isCompleted.value) {
      await completedLessonClient.uncompleteLesson(route.params.leccion);
    } else {
      await completedLessonClient.completeLesson(route.params.leccion);
    }
    // Update UI state after successful API call
    isCompleted.value = !isCompleted.value;
  } catch (error) {
    console.error("Failed to toggle lesson completion:", error);
  } finally {
    isCompleting.value = false;
  }
};

const openNotesModal = () => notesModalRef.value.show();
const saveNotes = (notes) => { userNotes.value = notes; };


// Watch for route changes
watch(
  () => route.params,
  (newParams) => {
    if (newParams.curso) {
      fetchData();
    }
  },
  { immediate: true }
);

/*
onMounted(async () => {
  await fetchData();
});*/
</script>

<template>
  <div class="p-2 p-lg-4 container-fluid">
    <div v-if="loading" class="text-center py-10 my-10">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <div v-else class="card">
      <div class="card-header py-3 bg-white">
        <div class="d-flex justify-content-between align-items-center">
          <router-link :to="`/cursos/${course?.id}`" class="custom-hover-underline text-decoration-none text-reset">
            <h6 class="mb-0">
              <i class="fas fa-arrow-alt-circle-left fa-lg me-2"></i>
              {{ course?.titulo }}
            </h6>
          </router-link>



          <!-- Completion Button -->
          <div>
            <button class="btn w-100 mb-0 py-2" :class="isCompleted ? 'btn-success' : 'btn-outline-dark'"
              @click="toggleComplete"
              :disabled="isCompleting">
               <span
                  v-if="isCompleting"
                  class="spinner-border spinner-border-sm me-1"
                ></span>
                <i v-else :class="isCompleted ? 'fas fa-check-circle me-2' : 'far fa-circle me-2'"></i>
              {{ isCompleted ? 'Completado' : 'Completar' }}
            </button>
          </div>
        </div>

        <div v-if="currentChapter" class="d-flex justify-content-between align-items-center">
          <p class="text-secondary font-weight-bold text-xs mt-0 mb-0">
            Capítulo {{ currentChapter.orden }}: {{ currentChapter.titulo }}
          </p>

        </div>
      </div>

      <div class="card-body p-2 p-md-3">
        <div class="row">
          <!-- Main Content Area -->
          <div class="col-xl-8 col-lg-7">         
            <div v-if="forbidden" class="card border-warning">
              <div class="card-body text-center py-5">
                <div class="lock-icon mb-3">
                  <i class="fas fa-lock fa-4x text-primary"></i>
                </div>
                <h4 class="card-title fw-bold text-primary mb-3">Quiz Bloqueado</h4>
                <p class="card-text text-muted mb-4">                
                  <template>
                    Debes completar este quiz para continuar con el siguiente contenido.
                  </template>
                </p>
                
                <div class="requirements-card bg-light rounded p-3 mx-auto" style="max-width: 400px;">
                  <h6 class="fw-semibold mb-3">Requisitos para desbloquear:</h6>
                  <ul class="list-unstyled text-start">
                    <li class="mb-2">
                      <i class="fas fa-check-circle text-success me-2"></i>
                      Completar los contenido previo
                    </li>                               
                  </ul>
                </div>           
              </div>
            </div>

            <div v-else>            
            <!-- Video Player -->
            <div v-if="files.length > 0" class="video-container mt-0 mb-2">
              <video controls controlsList="nodownload" autoplay muted class="w-100 rounded shadow"
                oncontextmenu="return false;">
                <source :src="files[0].downloadUrl" type="video/mp4">
                Video no disponible.
              </video>

            </div>

            <!-- Lesson Content -->
            <div v-if="lesson" class="lesson-content mt-0 p-2 p-md-3">
              <h4 class="mb-3">{{ lesson.titulo }}</h4>
              <div class="lesson-descripcion mb-3 px-2 px-md-3">
                <h6 class="fw-bold">Descripción:</h6>
                <div v-html="lesson.contenido_texto"></div>
                <div v-if="!lesson.contenido_texto" class="text-center py-4">
                  <p class="text-muted">No hay descripción para esta lección.
                  </p>
                </div>
              </div>
              <hr class="horizontal dark mt-4 mb-4" />

              <!-- Resources section -->
              <div class="resources-section mt-2 mt-md-4 px-2 px-md-3">
                  <h6 class="">Recursos:</h6>

                <div v-if="loadingResources" class="text-center py-2">
                  <div class="spinner-border spinner-border-sm" role="status">
                    <span class="visually-hidden">Cargando...</span>
                  </div>
                </div>

                <div v-else-if="resources.length" class="resources-list">
                  <div class="list-group">
                    <a v-for="resource in resources" :key="resource.id" :href="resource.downloadUrl" download
                      class="list-group-item list-group-item-action d-flex align-items-center">
                      <i :class="getFileIcon(resource.type)" class="me-3 fa-lg"></i>
                      <div class="flex-grow-1">
                        <h6 class="mb-0">{{ resource.name }}</h6>
                      </div>
                      <i class="fas fa-download text-primary"></i>
                    </a>
                  </div>
                </div>
                <div v-else class="text-center py-4">
                  <p class="text-muted">No hay recursos disponibles para esta lección.
                    
                  </p>
                </div>
              </div>
            </div>
            </div>
          </div>

          <!-- Sidebar Navigation -->
          <div class="col-xl-4 col-lg-5">
            <div class="position-sticky top-10">
                <chapter-page :chapter-id="route.params.capitulo" :course-id="route.params.curso" />      
            </div>

          </div>
        </div>
      </div>
    </div>

    <button class="btn btn-primary rounded-circle p-3 position-fixed" style="   
        border-radius: 50%;
        bottom: 10px;
        right: 10px;
        font-size: 1.25rem;
        width: 50px;
        height: 50px;
        display: flex;
        justify-content: center;
        z-index: 990;
        box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.16);
        cursor: pointer;" @click="openNotesModal" title="Abrir Notas">
      <i class="fas fa-sticky-note"></i>
    </button>

    <notes-modal ref="notesModalRef" :initial-notes="userNotes" :course-id="courseId" @saveNotes="saveNotes" />

  </div>
</template>

<style scoped>
.video-container {
  position: relative;
  padding-bottom: 56.25%;
  /* 16:9 Aspect Ratio */
  height: 0;
  overflow: hidden;
}

.video-container video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.lesson-content,
.lesson-descripcion {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 0.5rem;
}

.lesson-descripcion {
  background-color: #f8f9fa;
  border-left: 4px solid #102d58;
  padding: 0.4rem 1rem;
  border-radius: 0.25rem;
  min-height: 100px;

}

.quiz-instructions {
  border-left: 4px solid #0dfd69;
}

.sticky-top {
  position: sticky;
  top: 1rem;
}

.accordion-item {
  border-bottom: 1px solid #dee2e6;
}

.accordion-button:not(.collapsed) {
  background-color: #f8f9fa;
}

.dropdown-menu {
  max-height: 300px;
  overflow-y: auto;
}

.card-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}

.bg-light {
  background-color: rgba(var(--bs-primary-rgb), 0.1) !important;
}

.resources-section {
  background-color: #f8f9fa;
  border-left: 4px solid #102d58;
  padding: 0.4rem 1rem;
  border-radius: 0.25rem;
  min-height: 150px;
}

.resources-list {
  border-radius: 0.5rem;
  overflow: hidden;
}

.list-group-item {
  transition: all 0.2s ease;
  border-left: none;
  border-right: none;
}

.list-group-item:first-child {
  border-top: none;
}

.list-group-item:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.text-muted {
  font-size: 0.8rem;
}

.custom-hover-underline:hover h6 {
  text-decoration: underline;
}
</style>